import { Box } from 'react-scale/components/pages/homepage/build-ai/box';
import { Tab as HeadlessuiTab } from '@headlessui/react';
import cn from 'classnames';
import React, { useRef, useEffect, useState, PropsWithChildren } from 'react';
import { gsap } from 'react-scale/lib/gsap';
import CTA from 'react-scale/components/elements/cta';
import s from './data-box.module.scss';
import { Tab3D } from 'react-scale/components/pages/homepage/build-ai/tabs/content/3d';
import { TabImage } from 'react-scale/components/pages/homepage/build-ai/tabs/content/image';
import { TabMapping } from 'react-scale/components/pages/homepage/build-ai/tabs/content/mapping';
import { TabText } from 'react-scale/components/pages/homepage/build-ai/tabs/content/text';
import { TabAudio } from 'react-scale/components/pages/homepage/build-ai/tabs/content/audio';
import { TabVideo } from 'react-scale/components/pages/homepage/build-ai/tabs/content/video';
import { url } from 'inspector';
import { string } from 'prop-types';

type DataBoxProps = {
  title: string;
  subtitle: string;
  body: string;
  cta: Pick<
    { theme: any; cta: any; themeColors: any; center: any; size: any },
    'size' | 'themeColors'
  >;
  '3d': { label?: string } | null;
  mapping: { label?: string } | null;
  text: { label?: string } | null;
  audio: { label?: string } | null;
  image: { label?: string; url: string; alt: string } | null;
  video: { label?: string; url: string; poster: string } | null;
};

export const DataBox: React.FC<DataBoxProps> = ({ title, subtitle, body, cta, ...rest }) => {
  const tabs = React.useMemo(() => {
    const craftedTabs = [];

    if (rest['3d']?.label)
      craftedTabs.push({
        title: rest['3d']?.label,
        content: <Tab3D />
      });
    if (rest.image?.label)
      craftedTabs.push({
        title: rest.image?.label,
        content: <TabImage url={rest.image?.url ?? ''} alt={rest.image?.alt} />
      });
    if (rest.video?.label)
      craftedTabs.push({
        title: rest.video?.label,
        content: <TabVideo url={rest.video?.url ?? ''} poster={rest.video?.poster} />
      });
    if (rest.mapping?.label)
      craftedTabs.push({
        title: rest.mapping?.label,
        content: <TabMapping />
      });
    if (rest.text?.label)
      craftedTabs.push({
        title: rest.text?.label,
        content: <TabText />
      });
    if (rest.audio?.label)
      craftedTabs.push({
        title: rest.audio?.label,
        content: <TabAudio />
      });

    return craftedTabs;
  }, [rest]);

  return (
    <Box title={title} subtitle={subtitle}>
      <div className="mt-6">
        <AnnotationTabs tabs={tabs}>
          <p className="mb-8 text-sm md:text-base text-black sm:text-white">{body}</p>
          {/* @ts-ignore: Unreachable code error */}
          <CTA cta={cta} />
        </AnnotationTabs>
      </div>
    </Box>
  );
};

const AnnotationTabs: React.FC<
  PropsWithChildren<{ tabs: { title: string; content: React.ReactNode }[] }>
> = ({ children, tabs = [] }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const cardsRef = useRef<HTMLDivElement[] | HTMLElement[]>([]);
  const markerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (!cardsRef.current || cardsRef.current.length < 1 || !markerRef.current) return;

    const tl = gsap.timeline({ autoRemoveChildren: true, smoothChildTiming: true });

    tl.to(markerRef.current, {
      left: cardsRef.current[selectedTabIndex].offsetLeft,
      width: cardsRef.current[selectedTabIndex].offsetWidth,
      height: cardsRef.current[selectedTabIndex].offsetHeight,
      visibility: 'visible',
      duration: 0.3,
      ease: 'Power2.inOut'
    });
  }, [selectedTabIndex]);

  return (
    <HeadlessuiTab.Group onChange={index => setSelectedTabIndex(index)}>
      <div className="flex flex-col-reverse items-start lg:flex-row">
        <div className="relative no-scrollbar sm:mx-0 lg:w-[47%] overflow-visible xl:w-auto max-w-full">
          <div className={cn('px-8 mb-8 -mx-8 overflow-x-auto', tabs.length < 2 && 'hidden')}>
            <HeadlessuiTab.List className={s.tabs}>
              <div
                ref={markerRef}
                className="absolute w-20 h-20 bg-black sm:bg-white rounded-full invisible"
              />

              {tabs.map(({ title }, index) => (
                <HeadlessuiTab
                  key={index}
                  ref={el => (cardsRef.current = [...cardsRef.current, el])}
                  className={({ selected }) =>
                    cn(
                      'rounded-full py-2 px-4 relative transition-colors duration-300 focus:outline-none',
                      selected ? 'text-white sm:text-black' : 'text-black sm:text-white'
                    )
                  }
                >
                  {title}
                </HeadlessuiTab>
              ))}
            </HeadlessuiTab.List>
          </div>

          {children}
        </div>

        <HeadlessuiTab.Panels className="w-full lg:w-auto">
          {tabs.map(({ content }, index) => (
            <HeadlessuiTab.Panel key={index}>{content}</HeadlessuiTab.Panel>
          ))}
        </HeadlessuiTab.Panels>
      </div>
    </HeadlessuiTab.Group>
  );
};
